import { apiBaseUrl } from "./auth";
import { getStoredLanguage } from "../context/I18nContext";
import i18n from "../i18n";

const LESSONS_BASE = `${apiBaseUrl}/api/lessons`;

async function request(
  path,
  { accessToken, method = "GET", body, params } = {},
) {
  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
    "Accept-Language": getStoredLanguage(),
  };

  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const queryParams = params
    ? { ...params, lang: getStoredLanguage() }
    : { lang: getStoredLanguage() };
  const queryString = `?${new URLSearchParams(queryParams).toString()}`;

  const response = await fetch(`${LESSONS_BASE}${path}${queryString}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  const isJson = response.headers
    .get("content-type")
    ?.includes("application/json");
  const payload = isJson ? await response.json() : {};

  if (!response.ok) {
    const message = payload.error || i18n.t("errors.fetchFailed");
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }

  return payload;
}

export function fetchCatalog() {
  return request("/catalog/", { method: "GET" });
}

export function fetchLessons({ accessToken, params } = {}) {
  return request("/", {
    accessToken,
    params,
  });
}

export function fetchLesson({ accessToken, lessonId }) {
  if (!lessonId) {
    throw new Error(i18n.t("errors.lessonIdRequired"));
  }
  return request(`/${lessonId}/`, {
    accessToken,
  });
}

export function createLesson(accessToken, payload) {
  return request("/", {
    accessToken,
    method: "POST",
    body: payload,
  });
}

export function updateLesson(accessToken, lessonId, payload) {
  return request(`/${lessonId}/`, {
    accessToken,
    method: "PATCH",
    body: payload,
  });
}

export function deleteLesson(accessToken, lessonId) {
  return request(`/${lessonId}/`, {
    accessToken,
    method: "DELETE",
  });
}

export function fetchLessonMeeting({ accessToken, lessonId }) {
  if (!lessonId) {
    throw new Error(i18n.t("errors.lessonIdRequired"));
  }
  return request(`/${lessonId}/meeting/`, {
    accessToken,
  });
}

export function createLessonMeeting({ accessToken, lessonId }) {
  if (!lessonId) {
    throw new Error(i18n.t("errors.lessonIdRequired"));
  }
  return request(`/${lessonId}/meeting/`, {
    accessToken,
    method: "POST",
  });
}

export function createLessonParentNotification({
  accessToken,
  lessonId,
  status,
  message,
}) {
  if (!lessonId) {
    throw new Error(i18n.t("errors.lessonIdRequired"));
  }
  return request(`/${lessonId}/parent-notifications/`, {
    accessToken,
    method: "POST",
    body: {
      status,
      message,
    },
  });
}

export function fetchParentLessonNotifications({
  accessToken,
  unreadOnly,
} = {}) {
  return request(`/parent-notifications/`, {
    accessToken,
    params: unreadOnly ? { unread_only: "1" } : undefined,
  });
}

export function markParentLessonNotificationRead({
  accessToken,
  notificationId,
}) {
  if (!notificationId) {
    throw new Error("Notification id is required");
  }
  return request(`/parent-notifications/${notificationId}/read/`, {
    accessToken,
    method: "POST",
  });
}
